import { useState, useEffect, useRef, Fragment } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import AvisoUltimaNota from '../components/AvisoUltimaNota'
import NaturezaSelect from '../components/NaturezaSelect'
import ItemSelect from '../components/ItemSelect'
import IbsCbsSelect from '../components/IbsCbsSelect'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const UF_IBGE: Record<string, string> = {
  AC:'12',AL:'27',AP:'16',AM:'13',BA:'29',CE:'23',DF:'53',ES:'32',
  GO:'52',MA:'21',MT:'51',MS:'50',MG:'31',PA:'15',PB:'25',PR:'41',
  PE:'26',PI:'22',RJ:'33',RN:'24',RS:'43',RO:'11',RR:'14',SC:'42',
  SP:'35',SE:'28',TO:'17',
}
function calcDV(c43: string): number {
  const p = [2,3,4,5,6,7,8,9]; let s = 0
  for (let i = c43.length-1, j=0; i>=0; i--,j++) s += parseInt(c43[i]) * p[j%8]
  const r = s % 11; return r < 2 ? 0 : 11 - r
}
function gerarChave(empcgc: string, empest: string, dataEmissao: string, serie: string, numero: string, cNF: string) {
  const cuf = UF_IBGE[empest.trim()] ?? '43'
  const aamm = dataEmissao.replace(/-/g,'').slice(2,6)
  const cnpj = empcgc.replace(/\D/g,'').padStart(14,'0')
  const ser = serie.padStart(3,'0'); const num = numero.padStart(9,'0')
  const c43 = `${cuf}${aamm}${cnpj}55${ser}${num}1${cNF}`
  return c43 + calcDV(c43)
}
function randCNF() { return String(Math.floor(Math.random()*99999999)).padStart(8,'0') }
function formatDoc(doc: string) {
  const n = doc.replace(/\D/g,'')
  if (n.length===14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,'$1.$2.$3/$4-$5')
  if (n.length===11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,'$1.$2.$3-$4')
  return doc
}
const r2 = (n: number) => Math.round(n*100)/100
const brl = (n: number) => Number(n||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'})
const fmtMoeda = (v: string | number) => (typeof v === 'string' ? parseFloat(v) : v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtData = (iso: string | null) => iso ? iso.slice(0, 10).split('-').reverse().join('/') : '-'

// ─── Tipos de Nota de Débito (NT 2025.002-RTC) ─────────────────────────────────
//
// modo 'origem': item herdado de uma NF origem obrigatória (NFref) — 02, 03, 04.
// modo 'manual': destinatário pesquisado/fixo, item informado pelo usuário — 05, 06, 07.
// Regra B25-80: nenhum tipo de débito carrega tributos clássicos, EXCETO o 07
// (Perda em Estoque), que carrega ICMS/IPI/PIS/COFINS além do IBS/CBS.
// cstMode 'herdado': CST/cClassTrib travados, vindos da nota de origem (04).
// cstMode 'regra':   usuário escolhe a regra IBS/CBS (02, 03, 05, 06, 07) — a NT
//                    espera um CST/cClassTrib específico pra cada um, mas não
//                    travamos a escolha, só sinalizamos o esperado na tela.
// Tipo 01 (Transferência para Cooperativa) removido — nenhum cliente usa (2026-07-21).
// Tipo 02 (Anulação de Crédito): NF origem é uma nota de ENTRADA (a busca de NF
// origem já alcança entrada e saída — cli_cod é o CNPJ da outra parte nos dois
// casos). Destinatário, porém, é sempre o próprio emitente (não o fornecedor da
// entrada) — por isso usa destinatarioFixo apesar do modo 'origem'.
// Débito 07: destinatário é sempre o próprio emitente (sem busca), NF origem é
// opcional (só referência), e o valor unitário do item vem do custo médio de
// estoque (frota.stqscc, validado com o cliente — bate com o ERP legado).
// Tipo 08 (Desenquadramento SN) ainda depende de decisão do cliente sobre a UX
// do item fixo.

interface TipoDebitoConfig {
  codigo: string
  label: string
  habilitado: boolean
  motivo?: string
  modo: 'origem' | 'manual'
  cstMode: 'herdado' | 'regra'
  destinatarioLabel: string
  regraEsperada?: string
  // Regra B25-80: só o tipo 07 carrega tributos clássicos (ICMS/IPI/PIS/COFINS) além do IBS/CBS
  tributosClassicos?: boolean
  // 07 e 08: destinatário é sempre o próprio emitente, sem busca nenhuma
  destinatarioFixo?: 'emitente'
  // 07: NF origem é opcional (campo pra referenciar, mas não obrigatória nem herda nada)
  nfOrigemOpcional?: boolean
  // 07: valor unitário do item vem do custo médio de estoque (calculado, não digitado)
  custoMedioAutomatico?: boolean
  // Restringe a lista de Natureza/CFOP a códigos específicos (ex.: 06 só aceita 5922/6922,
  // não importa o tno_cod cadastrado — o que trava é o CFOP real, tno_cdf)
  cfopPermitidos?: number[]
  // Restringe a busca de regra IBS/CBS a cClassTrib específicos (07 só aceita 410030)
  ibsCbsTriPermitidos?: string[]
}

const TIPOS_DEBITO: TipoDebitoConfig[] = [
  { codigo: '02', label: '02 — Anulação de Crédito por Saídas Imunes/Isentas', habilitado: true, modo: 'origem', cstMode: 'regra', destinatarioLabel: 'Próprio emitente', destinatarioFixo: 'emitente', regraEsperada: 'CST 811 / cClassTrib 811001', ibsCbsTriPermitidos: ['811001'] },
  { codigo: '03', label: '03 — NF Não Processada na Apuração', habilitado: true, modo: 'origem', cstMode: 'regra', destinatarioLabel: 'Herdado da NF origem' },
  { codigo: '04', label: '04 — Multa e Juros', habilitado: true, modo: 'origem', cstMode: 'herdado', destinatarioLabel: 'Cliente original' },
  { codigo: '05', label: '05 — Transferência na Sucessão', habilitado: true, modo: 'manual', cstMode: 'regra', destinatarioLabel: 'Empresa sucessora', regraEsperada: 'CST 800 / cClassTrib 800001' },
  { codigo: '06', label: '06 — Pagamento Antecipado', habilitado: true, modo: 'manual', cstMode: 'regra', destinatarioLabel: 'Cliente do adiantamento', regraEsperada: 'CST 000 ou 100 (conforme a operação principal)', cfopPermitidos: [5922, 6922] },
  { codigo: '07', label: '07 — Perda em Estoque', habilitado: true, modo: 'manual', cstMode: 'regra', destinatarioLabel: 'Próprio emitente', regraEsperada: 'CST 410 / cClassTrib 410030', tributosClassicos: true, destinatarioFixo: 'emitente', nfOrigemOpcional: true, custoMedioAutomatico: true, cfopPermitidos: [5927], ibsCbsTriPermitidos: ['410030'] },
  // O item "Desenquadramento Simples Nacional" não é fixo no código — o Leandro
  // confirmou que cada cliente (cada implantação) cadastra o seu próprio item com
  // esse nome em frota.itscad e busca ele normalmente pelo ItemSelect (mesma lógica
  // dos tipos 01/05/06). Pode mudar de nome/código no futuro sem precisar mexer aqui.
  { codigo: '08', label: '08 — Desenquadramento do Simples Nacional', habilitado: true, modo: 'manual', cstMode: 'regra', destinatarioLabel: 'Próprio emitente', regraEsperada: 'CST 811 / cClassTrib 811003', destinatarioFixo: 'emitente' },
]

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Empresa {
  empcod: number; empnom: string; empcgc: string; empest: string
  empend?: string; empnum?: string; empbai?: string; empcid?: string; empcep?: string; emptel?: string; empins?: string
}
interface Natureza {
  tno_cod: number; tno_cdf: number; tno_des: string; tno_dsc: string; tno_nat: string
  tno_tic: string; tno_bic: number; tno_aic: number   // ICMS
  tno_tpi: string; tno_bpi: number; tno_api: number   // PIS
  tno_tcf: string; tno_bcf: number; tno_acf: number   // COFINS
  tno_tin: string; tno_bin: number; tno_ain: number   // IPI
}
interface IbsCbs {
  cst: string; classTrib: string; classTribDes: string
  cbsBc: number; cbsBcEf: number; cbsAliRef: number; cbsRedAli: number; cbsAliEf: number; cbsValor: number
  ibsUfEst: string; ibsUfBcEf: number; ibsUfAliRef: number; ibsUfRedAli: number; ibsUfAliEf: number; ibsUfValor: number
  ibsMunBcEf: number; ibsMunAliRef: number; ibsMunRedAli: number; ibsMunAliEf: number; ibsMunValor: number
  isSbas: number; isSali: number
}
const ibsCbsVazio: IbsCbs = {
  cst:'', classTrib:'', classTribDes:'',
  cbsBc:0, cbsBcEf:0, cbsAliRef:0, cbsRedAli:0, cbsAliEf:0, cbsValor:0,
  ibsUfEst:'', ibsUfBcEf:0, ibsUfAliRef:0, ibsUfRedAli:0, ibsUfAliEf:0, ibsUfValor:0,
  ibsMunBcEf:0, ibsMunAliRef:0, ibsMunRedAli:0, ibsMunAliEf:0, ibsMunValor:0,
  isSbas:0, isSali:0,
}
function calcIbsCbs(total: number, r: any): IbsCbs {
  if (!r || total<=0) return {...ibsCbsVazio}
  const bcEfC = r2(total*(1-Number(r.ibs08_crbc)/100))
  const bcEfU = r2(total*(1-Number(r.ibs08_urbc)/100))
  const bcEfM = r2(total*(1-Number(r.ibs08_mrbc)/100))
  return {
    cst: r.ibs08_cst?.trim()??'', classTrib: r.ibs08_tri?.trim()??'', classTribDes: r.ibs08_d02?.trim()??'',
    cbsBc: total, cbsBcEf: bcEfC, cbsAliRef: Number(r.ibs08_cali), cbsRedAli: Number(r.ibs08_cpra), cbsAliEf: Number(r.ibs08_caef), cbsValor: r2(bcEfC*Number(r.ibs08_caef)/100),
    ibsUfEst: r.ibs08_uest?.trim()??'', ibsUfBcEf: bcEfU, ibsUfAliRef: Number(r.ibs08_uali), ibsUfRedAli: Number(r.ibs08_upra), ibsUfAliEf: Number(r.ibs08_uaef), ibsUfValor: r2(bcEfU*Number(r.ibs08_uaef)/100),
    ibsMunBcEf: bcEfM, ibsMunAliRef: Number(r.ibs08_mali), ibsMunRedAli: Number(r.ibs08_mpra), ibsMunAliEf: Number(r.ibs08_maef), ibsMunValor: r2(bcEfM*Number(r.ibs08_maef)/100),
    isSbas: Number(r.ibs08_sbas), isSali: Number(r.ibs08_sali),
  }
}

interface Cabecalho {
  empcod: number; serie: string; numero: number
  cnpj_fornecedor: string; chave: string; data_emissao: string
  valor_total: number; cfop_original: string; nome_fornecedor: string
  fj: string; ie: string; uf: string; contribuinte: string
  logradouro: string; numero_end: string; bairro: string
  cep: string; telefone: string; cidade: string
  bloqueado?: boolean
}
interface ItemDebito {
  lin: number; its_cod: number; descricao: string; unidade: string
  quantidade: number; preco_unitario: number; ncm: string; cest: string
  selecionado: boolean; qtd_debito: string; ibsCbs: IbsCbs; expandido: boolean
  // Tipo 03 (Débitos de NF não Processadas): item pode vir de qualquer uma das
  // NFs de origem referenciadas (até 20), não só da primeira — usado no DFeReferenciado.
  origemChave?: string
}
interface Cliente {
  cli_cod: string; cli_mat: string; cli_nom: string; cli_fan: string
  cli_fj: string; cli_ins: string; cli_est: string; cli_contri: string
  bloqueado?: boolean
}
interface Endereco {
  logradouro: string; numero: string; bairro: string; cep: string
  telefone: string; email: string; cidade: string; uf: string
}
interface ItemManual {
  cProd: string; descricao: string; unidade: string; NCM: string; CEST: string
  quantidade: number; valorUnitario: number
  ibsCbsRegra: string; ibsCbsDes: string
  ibsCbs: IbsCbs
  // só usados quando tipoCfg.tributosClassicos (hoje só o Débito 07)
  edit_icms_cst: string; edit_icms_base: number; edit_icms_aliq: number; edit_icms_valor: number
  edit_ipi_cst: string; edit_ipi_base: number; edit_ipi_aliq: number; edit_ipi_valor: number
  edit_pis_cst: string; edit_pis_base: number; edit_pis_aliq: number; edit_pis_valor: number
  edit_cofins_cst: string; edit_cofins_base: number; edit_cofins_aliq: number; edit_cofins_valor: number
  // só usado quando tipoCfg.custoMedioAutomatico (Débito 07)
  saldoEstoque?: number
  buscandoCustoMedio?: boolean
}
const itemManualVazio: ItemManual = {
  cProd:'', descricao:'', unidade:'UN', NCM:'', CEST:'',
  quantidade:1, valorUnitario:0,
  ibsCbsRegra:'', ibsCbsDes:'',
  ibsCbs: { ...ibsCbsVazio },
  edit_icms_cst:'41', edit_icms_base:0, edit_icms_aliq:0, edit_icms_valor:0,
  edit_ipi_cst:'53', edit_ipi_base:0, edit_ipi_aliq:0, edit_ipi_valor:0,
  edit_pis_cst:'07', edit_pis_base:0, edit_pis_aliq:0, edit_pis_valor:0,
  edit_cofins_cst:'07', edit_cofins_base:0, edit_cofins_aliq:0, edit_cofins_valor:0,
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function EmitirNotaDebitoNFe() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')
  const idSalvoRef = useRef<number | null>(null) // nota já gravada nesta tela (reemissão depois de rejeição)

  const [etapa, setEtapa] = useState(0)

  const [tipoDebito, setTipoDebito] = useState('02')
  const tipoCfg = TIPOS_DEBITO.find(t => t.codigo === tipoDebito)!

  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa, setEmpresa]   = useState<Empresa | null>(null)

  // modo 'origem'
  const [chaveInput, setChaveInput] = useState('')
  const [empcod, setEmpcod] = useState('')
  const [cnpj, setCnpj]     = useState('')
  const [serie, setSerie]   = useState('')
  const [numBusca, setNumBusca] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [erroBusca, setErroBusca] = useState('')
  const [cab, setCab]           = useState<Cabecalho | null>(null)
  const [itens, setItens]       = useState<ItemDebito[]>([])
  const [ibsCbsCod, setIbsCbsCod] = useState('')
  const [ibsCbsDes, setIbsCbsDes] = useState('')

  // Tipo 03 (Débitos de NF não Processadas): NFs de origem além da primeira (até 20 no total,
  // limite confirmado com o Leandro — a NT permite até 990, mas não é usado na prática).
  const [notasAdicionais, setNotasAdicionais] = useState<Cabecalho[]>([])
  const [chaveAdicionalInput, setChaveAdicionalInput] = useState('')
  const [buscandoAdicional, setBuscandoAdicional] = useState(false)
  const [erroAdicional, setErroAdicional] = useState('')
  const LIMITE_NOTAS_ORIGEM = 20

  // Financeiro (Débito 04) — configurado localmente na revisão; só é gravado
  // (POST /nfe/:id/financeiro) depois que a nota autorizar, dentro de emitir().
  const [financeiroModal, setFinanceiroModal] = useState(false)
  const [finParcelas, setFinParcelas] = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  const [finContaCod, setFinContaCod] = useState('')
  const [finContaNome, setFinContaNome] = useState('')
  const [finContaResultados, setFinContaResultados] = useState<{ dep_cod: string; dep_des: string }[]>([])
  const [finContaAberto, setFinContaAberto] = useState(false)
  const [finContaErro, setFinContaErro] = useState<string | null>(null)
  const [finQtd, setFinQtd] = useState(1)
  const [finIntervalo, setFinIntervalo] = useState(30)

  // modo 'manual'
  const [clienteBusca, setClienteBusca] = useState('')
  const [clientesEncontrados, setClientesEncontrados] = useState<Cliente[]>([])
  const [buscandoCliente, setBuscandoCliente] = useState(false)
  const [clienteSel, setClienteSel] = useState<Cliente | null>(null)
  const [clienteEndereco, setClienteEndereco] = useState<Endereco | null>(null)
  const [itensManual, setItensManual] = useState<ItemManual[]>([{ ...itemManualVazio }])
  const debounceCliente = useRef<ReturnType<typeof setTimeout>>()
  // Débito 07: chave de NF origem opcional (só referência, não herda nada) e itens
  // com tributos clássicos calculados pela natureza + custo médio buscado do estoque
  const [nfOrigemOpcionalChave, setNfOrigemOpcionalChave] = useState('')

  const [proxSerie, setProxSerie]   = useState('')
  const [proxNumero, setProxNumero] = useState('')
  const [chaveNFe, setChaveNFe]     = useState('')

  const [naturezas, setNaturezas]   = useState<Natureza[]>([])
  const [naturezaCod, setNaturezaCod] = useState('')
  const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().slice(0,10))
  const [obs, setObs]               = useState('')

  const [enviando, setEnviando]   = useState(false)
  const [resultado, setResultado] = useState<string|null>(null)
  const [erro, setErro]           = useState<string|null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => {
      setEmpresas(r.data)
      if (r.data.length > 0) setEmpresa(r.data[0])
    }).catch(() => {})
    // Débito exige tpNF=1 (Saída, regra B25-120) — mostra qualquer natureza de saída
    // (CFOP 5xxx/6xxx), independente de tno_nat (esse campo só reflete se a natureza
    // está pré-associada à tela de Devolução, não se o CFOP é de devolução de verdade).
    api.get('/erp/naturezas').then(r => setNaturezas(r.data.filter((n: Natureza) => n.tno_cdf >= 5000 && n.tno_cdf < 7000))).catch(() => {})
  }, [])
  useEffect(() => {
    if (!empresa) return
    setEmpcod(String(empresa.empcod))
    // Cobre o tipo padrão selecionado ao carregar a página (destinatarioFixo='emitente')
    // — trocarTipo() só roda quando o usuário troca de tipo no dropdown, não na carga inicial.
    if (tipoCfg.destinatarioFixo === 'emitente') {
      setClienteSel({
        cli_cod: '', cli_mat: empresa.empcgc, cli_nom: empresa.empnom, cli_fan: '',
        cli_fj: 'J', cli_ins: empresa.empins ?? '', cli_est: empresa.empest, cli_contri: 'S', bloqueado: false,
      })
      // Em modo edição, número/chave vêm do payload salvo (efeito abaixo) — chamar
      // buscarProximoNumero aqui gera um número novo e some com o original.
      if (!editId) buscarProximoNumero(empresa)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa])

  // ── Modo edição: carrega payload da nota rejeitada/pendente ───────────────
  useEffect(() => {
    if (!editId || !empresas.length || !naturezas.length) return
    api.get(`/nfe/${editId}/payload`).then(r => {
      const fp = r.data?._formPayload
      if (!fp || fp._tipo !== 'NOTA_DEBITO') return
      const emp = empresas.find(e => e.empcod === fp.empcod) ?? empresas[0]
      setEmpresa(emp)
      setTipoDebito(fp.tipoDebito ?? '02')
      setCab(fp.cab ?? null)
      setItens(fp.itens ?? [])
      setNotasAdicionais(fp.notasAdicionais ?? [])
      setClienteSel(fp.clienteSel ?? null)
      setClienteEndereco(fp.clienteEndereco ?? null)
      setItensManual(fp.itensManual?.length ? fp.itensManual : [{ ...itemManualVazio }])
      setNfOrigemOpcionalChave(r.data?._dadosNFe?.nfRef?.[0] ?? '')
      setNaturezaCod(String(fp.naturezaCod ?? ''))
      setDataEmissao(fp.dataEmissao ?? new Date().toISOString().slice(0,10))
      setObs(fp.obs ?? '')
      setIbsCbsCod(fp.ibsCbsCod ?? '')
      setIbsCbsDes(fp.ibsCbsDes ?? '')
      setProxSerie(fp.proxSerie ?? '')
      setProxNumero(fp.proxNumero ?? '')
      setChaveNFe(fp.chaveNFe ?? '')
      setEtapa(1)
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, empresas.length, naturezas.length])

  function trocarTipo(cod: string) {
    setTipoDebito(cod)
    setCab(null); setItens([]); setNaturezaCod('')
    setNotasAdicionais([]); setChaveAdicionalInput(''); setErroAdicional('')
    setChaveInput(''); setErroBusca(''); setIbsCbsCod(''); setIbsCbsDes('')
    setClienteEndereco(null); setClienteBusca(''); setClientesEncontrados([])
    setItensManual([{ ...itemManualVazio }])
    setProxSerie(''); setProxNumero(''); setChaveNFe(''); setNfOrigemOpcionalChave('')

    const cfg = TIPOS_DEBITO.find(t => t.codigo === cod)!
    if (cfg.destinatarioFixo === 'emitente' && empresa) {
      setClienteSel({
        cli_cod: '', cli_mat: empresa.empcgc, cli_nom: empresa.empnom, cli_fan: '',
        cli_fj: 'J', cli_ins: empresa.empins ?? '', cli_est: empresa.empest, cli_contri: 'S', bloqueado: false,
      })
      buscarProximoNumero(empresa)
    } else {
      setClienteSel(null)
    }
  }

  async function buscarProximoNumero(empAtual: Empresa) {
    const nr = await api.get(`/erp/proximonumero/${empAtual.empcod}/NFE`)
    setProxSerie(String(nr.data.serie))
    setProxNumero(String(nr.data.numero))
    const cNF = randCNF()
    setChaveNFe(gerarChave(empAtual.empcgc, empAtual.empest, new Date().toISOString().slice(0,10), String(nr.data.serie), String(nr.data.numero), cNF))
  }

  // ── modo 'origem' ───────────────────────────────────────────────────────
  const chaveDigits      = chaveInput.replace(/\D/g,'')
  const podeBuscarChave  = chaveDigits.length === 44
  const podeBuscarCampos = !!(empcod && cnpj && serie && numBusca)
  const podeBuscar       = podeBuscarChave || podeBuscarCampos

  // Débito 04: valor é editável (não vem da qtd×preço da origem) — recalcula a base/valor
  // do IBS/CBS a partir do valor digitado, mantendo as alíquotas/CST/cClassTrib herdados.
  function recalcIbsCbsBase(ibs: IbsCbs, base: number): IbsCbs {
    return {
      ...ibs,
      cbsBcEf: base, cbsValor: r2(base * ibs.cbsAliEf / 100),
      ibsUfBcEf: base, ibsUfValor: r2(base * ibs.ibsUfAliEf / 100),
      ibsMunBcEf: base, ibsMunValor: r2(base * ibs.ibsMunAliEf / 100),
    }
  }
  function mapearItensOrigem(its: any[], origemChave: string): ItemDebito[] {
    return its.map((it: any) => {
      const ibsCbsOrigem: IbsCbs = (tipoCfg.cstMode === 'herdado' && it.ibs_cbs_cst) ? {
        cst: it.ibs_cbs_cst, classTrib: it.ibs_cbs_classtrib, classTribDes: it.ibs_cbs_classtrib_des ?? '',
        cbsBc: Number(it.cbs_base), cbsBcEf: Number(it.cbs_bc_ef), cbsAliRef: Number(it.cbs_ali_ref), cbsRedAli: Number(it.cbs_red_ali), cbsAliEf: Number(it.cbs_ali_ef), cbsValor: Number(it.cbs_valor),
        ibsUfEst: it.ibs_uf_est ?? '', ibsUfBcEf: Number(it.ibs_uf_bc_ef), ibsUfAliRef: Number(it.ibs_uf_ali_ref), ibsUfRedAli: Number(it.ibs_uf_red_ali), ibsUfAliEf: Number(it.ibs_uf_ali_ef), ibsUfValor: Number(it.ibs_uf_valor),
        ibsMunBcEf: Number(it.ibs_mun_bc_ef), ibsMunAliRef: Number(it.ibs_mun_ali_ref), ibsMunRedAli: Number(it.ibs_mun_red_ali), ibsMunAliEf: Number(it.ibs_mun_ali_ef), ibsMunValor: Number(it.ibs_mun_valor),
        isSbas: Number(it.is_base), isSali: Number(it.is_aliq),
      } : {...ibsCbsVazio}
      return {
        lin: it.lin, its_cod: it.its_cod, descricao: it.descricao, unidade: it.unidade,
        quantidade: Number(it.quantidade),
        preco_unitario: tipoDebito === '04' ? 0 : Number(it.preco_unitario),
        ncm: it.ncm, cest: it.cest,
        selecionado: true, qtd_debito: tipoDebito === '04' ? '1' : String(it.quantidade), expandido: true,
        origemChave,
        ibsCbs: tipoDebito === '04' ? recalcIbsCbsBase(ibsCbsOrigem, 0) : ibsCbsOrigem,
      }
    })
  }

  async function buscar() {
    setErroBusca(''); setCab(null); setItens([]); setProxNumero(''); setChaveNFe('')
    setNotasAdicionais([]); setChaveAdicionalInput(''); setErroAdicional('')
    setBuscando(true)
    try {
      const url = podeBuscarChave
        ? `/nfe/origem/buscar?chave=${chaveDigits}`
        : `/nfe/origem/buscar?empcod=${empcod}&cnpj=${cnpj.replace(/\D/g,'')}&serie=${serie}&numero=${numBusca}`
      const r = await api.get(url)
      const { cabecalho, itens: its } = r.data
      setCab(cabecalho)
      setItens(mapearItensOrigem(its, cabecalho.chave))
      const empAtual = empresa ?? empresas.find(e => e.empcod === cabecalho.empcod)
      if (empAtual) await buscarProximoNumero(empAtual)
    } catch (e: any) {
      setErroBusca(e.response?.data?.error ?? 'Nota não encontrada')
    } finally { setBuscando(false) }
  }

  // Tipo 03: adiciona mais uma NF de origem (até LIMITE_NOTAS_ORIGEM no total) — os itens
  // dela entram na mesma lista, cada um marcando de qual chave veio (origemChave).
  async function buscarNfAdicional() {
    const digits = chaveAdicionalInput.replace(/\D/g, '')
    if (digits.length !== 44) { setErroAdicional('Informe uma chave de 44 dígitos.'); return }
    const totalChaves = 1 + notasAdicionais.length
    if (totalChaves >= LIMITE_NOTAS_ORIGEM) { setErroAdicional(`Limite de ${LIMITE_NOTAS_ORIGEM} notas de origem atingido.`); return }
    if (digits === cab?.chave || notasAdicionais.some(n => n.chave === digits)) { setErroAdicional('Essa NF já foi adicionada.'); return }
    setErroAdicional(''); setBuscandoAdicional(true)
    try {
      const r = await api.get(`/nfe/origem/buscar?chave=${digits}`)
      const { cabecalho, itens: its } = r.data
      if (cabecalho.cnpj_fornecedor.replace(/\D/g,'') !== cab?.cnpj_fornecedor.replace(/\D/g,'')) {
        setErroAdicional('Essa NF é de um fornecedor diferente da primeira — não pode entrar na mesma nota de débito.')
        return
      }
      setNotasAdicionais(prev => [...prev, cabecalho])
      setItens(prev => [...prev, ...mapearItensOrigem(its, cabecalho.chave)])
      setChaveAdicionalInput('')
    } catch (e: any) {
      setErroAdicional(e.response?.data?.error ?? 'Nota não encontrada')
    } finally { setBuscandoAdicional(false) }
  }

  function removerNfAdicional(chave: string) {
    setNotasAdicionais(prev => prev.filter(n => n.chave !== chave))
    setItens(prev => prev.filter(it => it.origemChave !== chave))
  }

  // Tipo 03 (cstMode='regra'): uma única regra IBS/CBS escolhida pelo usuário, aplicada a todos os itens
  async function atualizarIbsCbsOrigem(cod: string, des: string) {
    setIbsCbsCod(cod); setIbsCbsDes(des)
    if (!cod) { setItens(prev => prev.map(it => ({...it, ibsCbs: {...ibsCbsVazio}}))); return }
    try {
      const r = await api.get(`/erp/ibscbs/${cod}`)
      setItens(prev => prev.map(it => {
        const qtd = Number(it.qtd_debito)||0
        const total = r2(qtd * Number(it.preco_unitario))
        return {...it, ibsCbs: calcIbsCbs(total, r.data)}
      }))
    } catch {}
  }

  function editItem(idx: number, field: string, value: any) {
    setItens(prev => prev.map((it, i) => {
      if (i !== idx) return it
      const updated = { ...it, [field]: value }
      if (tipoDebito === '04' && field === 'preco_unitario') {
        updated.ibsCbs = recalcIbsCbsBase(it.ibsCbs, Number(value) || 0)
      }
      return updated
    }))
  }

  const itensSel = itens.filter(it => it.selecionado && (Number(it.qtd_debito)||0) > 0)
  const temQtdInvalida = itens.some(it => it.selecionado && (Number(it.qtd_debito)||0) > it.quantidade)
  const totalOrigem = itensSel.reduce((s,it) => s + r2((Number(it.qtd_debito)||0)*Number(it.preco_unitario)), 0)

  const nat = naturezas.find(n => String(n.tno_cod) === naturezaCod)

  // ── modo 'manual' ───────────────────────────────────────────────────────
  function handleBuscaCliente(valor: string) {
    setClienteBusca(valor); setClienteSel(null); setClienteEndereco(null)
    if (debounceCliente.current) clearTimeout(debounceCliente.current)
    if (valor.length < 2) { setClientesEncontrados([]); return }
    debounceCliente.current = setTimeout(async () => {
      setBuscandoCliente(true)
      try { const r = await api.get('/erp/clientes', { params: { busca: valor } }); setClientesEncontrados(r.data) }
      finally { setBuscandoCliente(false) }
    }, 400)
  }

  async function selecionarCliente(c: Cliente) {
    setClienteSel(c); setClienteBusca(c.cli_nom); setClientesEncontrados([])
    try {
      const r = await api.get(`/erp/clientes/${c.cli_cod}/endereco`)
      setClienteEndereco(r.data)
    } catch {}
    const empAtual = empresa ?? empresas[0]
    if (empAtual) await buscarProximoNumero(empAtual)
  }

  function addItemManual() { setItensManual(prev => [...prev, { ...itemManualVazio }]) }
  function removeItemManual(i: number) { setItensManual(prev => prev.filter((_, idx) => idx !== i)) }
  function updateItemManual(i: number, campo: keyof ItemManual, valor: any) {
    setItensManual(prev => prev.map((it, idx) => idx === i ? { ...it, [campo]: valor } : it))
  }

  // Tributos clássicos por natureza — só usado quando tipoCfg.tributosClassicos (Débito 07)
  function calcImpostosNatManual(n: Natureza, vProd: number) {
    const bc  = (base: number, p: number) => r2(base * (Number(p) / 100))
    const det = (base: number, a: number) => r2(base * (Number(a) / 100))
    const icmsBase   = bc(vProd, n.tno_bic);  const icmsValor   = det(icmsBase,   n.tno_aic)
    const ipiBase    = bc(vProd, n.tno_bin);  const ipiValor    = det(ipiBase,    n.tno_ain)
    const pisBase    = bc(vProd, n.tno_bpi);  const pisValor    = det(pisBase,    n.tno_api)
    const cofinsBase = bc(vProd, n.tno_bcf);  const cofinsValor = det(cofinsBase, n.tno_acf)
    return {
      edit_icms_cst: n.tno_tic?.trim() || '41',
      edit_icms_base: icmsBase, edit_icms_aliq: Number(n.tno_aic), edit_icms_valor: icmsValor,
      edit_ipi_cst: n.tno_tin?.trim() || '53',
      edit_ipi_base: ipiBase,   edit_ipi_aliq:  Number(n.tno_ain), edit_ipi_valor:  ipiValor,
      edit_pis_cst: n.tno_tpi?.trim() || '07',
      edit_pis_base: pisBase,   edit_pis_aliq:  Number(n.tno_api), edit_pis_valor:  pisValor,
      edit_cofins_cst: n.tno_tcf?.trim() || '07',
      edit_cofins_base: cofinsBase, edit_cofins_aliq: Number(n.tno_acf), edit_cofins_valor: cofinsValor,
    }
  }

  // Recalcula tributos clássicos dos itens manuais quando a natureza muda (só Débito 07)
  useEffect(() => {
    if (!nat || !tipoCfg.tributosClassicos || !itensManual.length) return
    setItensManual(prev => prev.map(it => {
      const vProd = r2(it.quantidade * it.valorUnitario)
      return { ...it, ...calcImpostosNatManual(nat, vProd) }
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturezaCod])

  // Débito 07: busca o saldo/custo médio do item selecionado em frota.stqscc
  async function buscarCustoMedio(i: number, cod: string) {
    if (!cod) return
    updateItemManual(i, 'buscandoCustoMedio', true)
    try {
      const r = await api.get(`/erp/itens/${cod}/saldo`)
      setItensManual(prev => prev.map((x, idx) => idx === i ? {
        ...x, valorUnitario: Number(r.data?.custoMedioUnitario ?? 0), saldoEstoque: Number(r.data?.saldoQtd ?? 0),
      } : x))
    } catch {
    } finally {
      updateItemManual(i, 'buscandoCustoMedio', false)
    }
  }

  const totalManual = itensManual.reduce((s, it) => s + it.quantidade * it.valorUnitario, 0)
  const itensManualValidos = itensManual.filter(it => it.cProd && it.quantidade > 0)

  async function emitir() {
    const empAtual = empresa ?? empresas.find(e => e.empcod === cab?.empcod)
    if (!empAtual || !nat) return

    if (tipoCfg.modo === 'origem') {
      if (!cab) return
      // Débito 02: destinatário é o próprio emitente, não o fornecedor da NF de
      // origem — bloqueio do fornecedor não se aplica aqui.
      if (tipoCfg.destinatarioFixo !== 'emitente' && cab.bloqueado) { setErro('Destinatário bloqueado — não é possível emitir documentos para ele.'); return }
    } else {
      if (!clienteSel) return
      if (clienteSel.bloqueado) { setErro('Destinatário bloqueado — não é possível emitir documentos para ele.'); return }
    }
    setEnviando(true); setErro(null)

    const cNF  = randCNF()
    const chave = gerarChave(empAtual.empcgc, empAtual.empest, dataEmissao, proxSerie, proxNumero, cNF)
    const chaveUsada = editId ? chaveNFe : chave

    let destinatario: any
    let indIEDest: '1'|'2'|'9'
    let idDest: '1'|'2'|'3'
    let itensNFe: any[]
    let nfRef: string[] | undefined

    const impostosVazios = {
      icmsCst: '41', icmsOrig: '0', icmsBase: 0, icmsAliq: 0, icmsDest: 0,
      ipiCst: '53', pisCst: '07', pisBase: 0, pisAliq: 0, pisDest: 0,
      cofinsCst: '07', cofinsBase: 0, cofinsAliq: 0, cofinsDest: 0,
    }

    if (tipoCfg.modo === 'origem') {
      if (tipoCfg.destinatarioFixo === 'emitente') {
        // Débito 02: a NF origem é uma nota de ENTRADA (cli_cod = fornecedor).
        // O destinatário desta Nota de Débito, porém, é a própria empresa —
        // não o fornecedor que consta na nota de origem.
        const c = clienteSel!
        indIEDest = c.cli_ins?.trim() ? '1' : c.cli_contri === 'S' ? '2' : '9'
        idDest = '1'
        destinatario = {
          doc: c.cli_mat.replace(/\D/g, ''), fj: c.cli_fj as 'J'|'F',
          nome: c.cli_nom, ie: c.cli_ins?.trim() || undefined, indIEDest,
          endereco: {
            logradouro: empAtual.empend ?? '', numero: empAtual.empnum ?? 'S/N', bairro: empAtual.empbai ?? '',
            cidade: empAtual.empcid ?? '', uf: empAtual.empest, cep: empAtual.empcep ?? '', telefone: empAtual.emptel || undefined,
          },
        }
      } else {
        const contri = cab!.contribuinte?.trim().toUpperCase()
        indIEDest = cab!.ie?.trim() ? '1' : contri==='S' ? '2' : '9'
        idDest = cab!.uf?.trim().toUpperCase() === empAtual.empest?.trim().toUpperCase() ? '1' : '2'
        destinatario = {
          doc: cab!.cnpj_fornecedor, fj: (cab!.fj?.trim().toUpperCase()==='F'?'F':'J') as 'J'|'F',
          nome: cab!.nome_fornecedor, ie: cab!.ie||undefined, indIEDest,
          endereco: { logradouro: cab!.logradouro, numero: cab!.numero_end, bairro: cab!.bairro, cidade: cab!.cidade, uf: cab!.uf, cep: cab!.cep, telefone: cab!.telefone||undefined },
        }
      }
      itensNFe = itensSel.map(it => {
        const qtd   = Number(it.qtd_debito)||0
        const vProd = r2(qtd * Number(it.preco_unitario))
        return {
          cProd: String(it.its_cod), cEAN: 'SEM GTIN',
          xProd: it.descricao, NCM: it.ncm, CEST: it.cest||undefined,
          CFOP: String(nat.tno_cdf), uCom: it.unidade,
          qCom: qtd, vUnCom: Number(it.preco_unitario), vProd, indTot: '1' as const,
          dfeRefNItem: it.lin,
          dfeRefChave: it.origemChave || cab!.chave,
          impostos: {
            ...impostosVazios,
            ibsCbsCst:       it.ibsCbs.cst||undefined,
            ibsCbsClassTrib: it.ibsCbs.classTrib||undefined,
            cbsBcEf:   it.ibsCbs.cbsBcEf||undefined,  cbsAliEf:   it.ibsCbs.cbsAliEf||undefined,  cbsValor:   it.ibsCbs.cbsValor||undefined,
            ibsUfBcEf: it.ibsCbs.ibsUfBcEf||undefined, ibsUfAliEf: it.ibsCbs.ibsUfAliEf||undefined, ibsUfValor: it.ibsCbs.ibsUfValor||undefined,
            ibsMunBcEf:it.ibsCbs.ibsMunBcEf||undefined,ibsMunAliEf:it.ibsCbs.ibsMunAliEf||undefined,ibsMunValor:it.ibsCbs.ibsMunValor||undefined,
          },
        }
      })
      // Tipo 03 pode referenciar várias NFs de origem (até 20) — cada item já carrega
      // sua própria dfeRefChave; nfRef aqui vira a lista de todas as chaves distintas usadas.
      nfRef = [...new Set(itensSel.map(it => it.origemChave || cab!.chave))]
    } else {
      const c = clienteSel!
      indIEDest = c.cli_ins?.trim() ? '1' : c.cli_contri === 'S' ? '2' : '9'
      // Destinatário fixo (07/08): não há busca de endereço de cliente — o endereço é o da própria empresa.
      idDest = tipoCfg.destinatarioFixo === 'emitente' ? '1'
        : (clienteEndereco?.uf ?? c.cli_est)?.trim().toUpperCase() === empAtual.empest?.trim().toUpperCase() ? '1' : '2'
      destinatario = {
        doc: c.cli_mat.replace(/\D/g, ''), fj: c.cli_fj as 'J'|'F',
        nome: c.cli_nom?.trim() || c.cli_fan, ie: c.cli_ins?.trim() || undefined, indIEDest,
        endereco: tipoCfg.destinatarioFixo === 'emitente' ? {
          logradouro: empAtual.empend ?? '', numero: empAtual.empnum ?? 'S/N', bairro: empAtual.empbai ?? '',
          cidade: empAtual.empcid ?? '', uf: empAtual.empest, cep: empAtual.empcep ?? '', telefone: empAtual.emptel || undefined,
        } : clienteEndereco ? {
          logradouro: clienteEndereco.logradouro, numero: clienteEndereco.numero, bairro: clienteEndereco.bairro,
          cidade: clienteEndereco.cidade, uf: clienteEndereco.uf, cep: clienteEndereco.cep,
          telefone: clienteEndereco.telefone, email: clienteEndereco.email,
        } : undefined,
      }
      itensNFe = itensManualValidos.map(it => ({
        cProd: it.cProd || '0', cEAN: 'SEM GTIN',
        xProd: it.descricao, NCM: it.NCM.replace(/\D/g,'').padStart(8,'0'), CEST: it.CEST||undefined,
        CFOP: String(nat.tno_cdf), uCom: it.unidade,
        qCom: it.quantidade, vUnCom: it.valorUnitario, vProd: r2(it.quantidade*it.valorUnitario), indTot: '1' as const,
        impostos: {
          ...(tipoCfg.tributosClassicos ? {
            icmsCst: it.edit_icms_cst||'41', icmsOrig: '0',
            icmsBase: it.edit_icms_base, icmsAliq: it.edit_icms_aliq, icmsDest: it.edit_icms_valor,
            ipiCst: it.edit_ipi_cst||'53', ipiBase: it.edit_ipi_base||undefined, ipiAliq: it.edit_ipi_aliq||undefined, ipiDest: it.edit_ipi_valor||undefined,
            pisCst: it.edit_pis_cst||'07', pisBase: it.edit_pis_base, pisAliq: it.edit_pis_aliq, pisDest: it.edit_pis_valor,
            cofinsCst: it.edit_cofins_cst||'07', cofinsBase: it.edit_cofins_base, cofinsAliq: it.edit_cofins_aliq, cofinsDest: it.edit_cofins_valor,
          } : impostosVazios),
          ibsCbsCst:       it.ibsCbs.cst||undefined,
          ibsCbsClassTrib: it.ibsCbs.classTrib||undefined,
          cbsBcEf:   it.ibsCbs.cbsBcEf||undefined,  cbsAliEf:   it.ibsCbs.cbsAliEf||undefined,  cbsValor:   it.ibsCbs.cbsValor||undefined,
          ibsUfBcEf: it.ibsCbs.ibsUfBcEf||undefined, ibsUfAliEf: it.ibsCbs.ibsUfAliEf||undefined, ibsUfValor: it.ibsCbs.ibsUfValor||undefined,
          ibsMunBcEf:it.ibsCbs.ibsMunBcEf||undefined,ibsMunAliEf:it.ibsCbs.ibsMunAliEf||undefined,ibsMunValor:it.ibsCbs.ibsMunValor||undefined,
        },
      }))
      nfRef = (tipoCfg.nfOrigemOpcional && nfOrigemOpcionalChave.length === 44) ? [nfOrigemOpcionalChave] : undefined
    }

    const formPayload = {
      _tipo: 'NOTA_DEBITO',
      empcod: empAtual.empcod, tipoDebito,
      cab, itens, notasAdicionais, clienteSel, clienteEndereco, itensManual,
      naturezaCod, dataEmissao, obs: obs.trim()||'',
      proxSerie, proxNumero, chaveNFe: chaveUsada,
      ibsCbsCod, ibsCbsDes,
    }

    // Débito 04 (Multa e Juros) é o único tipo que gera fatura (regra confirmada com
    // o cliente). O lançamento em si (parcelas/conta) é feito depois, na tela "Lista
    // NF-e" — mesmo botão "Financeiro" já usado pra qualquer NF-e autorizada — então
    // aqui só precisamos marcar geraFatura e carregar tPag/vPag reais (senão o backend
    // zera o pagamento, igual faz pros demais 13 tipos que não geram fatura).
    const geraFatura = tipoDebito === '04'
    const totalNota = tipoCfg.modo === 'origem' ? totalOrigem : totalManual

    const payload = {
      _formPayload: formPayload,
      empcod: empAtual.empcod, chave: chaveUsada, serie: proxSerie, numero: Number(proxNumero), dataEmissao,
      natureza: { cfop: nat.tno_cdf, descricao: nat.tno_des || nat.tno_dsc || 'NOTA DE DEBITO' },
      destinatario,
      // NT 2025.002-RTC, regra B25-120: Nota de Débito exige tpNF=1 (Saída)
      tpNF: '1' as const, idDest, finNFe: '6' as const, tpNFDebito: tipoDebito, geraFatura,
      indFinal: (destinatario.indIEDest === '1' ? '0' : '1') as '0'|'1', indPres: '0', modFrete: '9' as const,
      itens: itensNFe,
      tPag: geraFatura ? '14' : '90', vPag: geraFatura ? totalNota : 0, obs: obs.trim()||undefined,
      nfRef,
    }

    try {
      let id: number
      if (editId) {
        await api.put(`/nfe/${editId}`, payload)
        id = Number(editId)
      } else {
        const rs = await api.post('/nfe/salvar', { ...payload, _idSalvo: idSalvoRef.current ?? undefined })
        id = rs.data.id
        idSalvoRef.current = id
      }

      let autorizado = false
      try {
        const rt = await api.post(`/nfe/${id}/transmitir`)
        if (rt.data.autorizado) {
          autorizado = true
          setResultado(`✓ Autorizado — Protocolo ${rt.data.protocolo}`)
        } else if (rt.data.cStat === '204') {
          setResultado(`cStat 204: Nota já transmitida anteriormente.\n\nUse "Consultar SEFAZ" na lista de transmissão para recuperar o protocolo.`)
        } else {
          setResultado(`REJEITADO\n\ncStat ${rt.data.cStat}: ${rt.data.xMotivo}`)
        }
      } catch (et: any) {
        const detalhe = et?.response?.data?.error || et?.message || 'Erro'
        try {
          const rs2 = await api.get(`/nfe/${id}/status`)
          if (rs2.data.situacao==='AUTORIZADA') { autorizado=true; setResultado(`✓ Autorizado — Protocolo ${rs2.data.protocolo}`) }
          else setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`)
        } catch { setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`) }
      }

      if (autorizado && geraFatura && finParcelas.length > 0 && finContaCod) {
        try {
          await api.post(`/nfe/${id}/financeiro`, { parcelas: finParcelas, contaCod: finContaCod })
        } catch (ef: any) {
          console.warn('[Financeiro] Erro ao salvar financeiro:', ef?.response?.data?.error || ef?.message)
        }
      }

      setEtapa(2)
      if (autorizado) setTimeout(() => navigate('/nfe/lista'), 2500)
    } catch (e: any) {
      if (e?.response?.data?.codigo === 'NUMERO_EM_USO') setProxNumero(String(e.response.data.proximo))
      setErro(e?.response?.data?.error || 'Erro ao emitir')
    } finally { setEnviando(false) }
  }

  // ─── Etapa 2: resultado ───────────────────────────────────────────────────

  if (etapa === 2) {
    const autorizado = resultado?.startsWith('✓')
    return (
      <div style={{ maxWidth:600, margin:'40px auto', textAlign:'center' }}>
        <div style={{ background: autorizado?'#f0fdf4':'#fef2f2', border:`1px solid ${autorizado?'#86efac':'#fca5a5'}`, borderRadius:8, padding:'32px 24px' }}>
          <div style={{ fontSize:'2rem', marginBottom:8 }}>{autorizado ? '✓' : '✗'}</div>
          <div style={{ fontWeight:700, fontSize:'1rem', color: autorizado?'#15803d':'#dc2626', marginBottom:12, whiteSpace:'pre-wrap' }}>
            {resultado}
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/nfe/lista')}>Ver Lista NF-e</button>
            {!autorizado && (
              <button className="btn btn-primary" onClick={() => { setEtapa(0); setCab(null); setItens([]); setClienteSel(null); setResultado(null) }}>
                Nova Nota de Débito
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ─── Financeiro (Débito 04) ────────────────────────────────────────────────

  function abrirFinanceiro() {
    const total = tipoCfg.modo === 'origem' ? totalOrigem : totalManual
    const base = new Date(dataEmissao)
    setFinParcelas(prev => prev.length ? prev : Array.from({ length: finQtd }, (_, i) => {
      const d = new Date(base)
      d.setDate(d.getDate() + finIntervalo * (i + 1))
      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (total / finQtd).toFixed(2) }
    }))
    setFinContaErro(null)
    setFinanceiroModal(true)
  }

  function recalcularParcelas(qtd: number, intervalo: number) {
    const base = new Date(dataEmissao)
    const total = tipoCfg.modo === 'origem' ? totalOrigem : totalManual
    setFinParcelas(Array.from({ length: qtd }, (_, i) => {
      const d = new Date(base)
      d.setDate(d.getDate() + intervalo * (i + 1))
      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (total / qtd).toFixed(2) }
    }))
  }

  // ─── Etapa 1: Revisão ─────────────────────────────────────────────────────

  if (etapa === 1 && (tipoCfg.modo === 'origem' ? cab : clienteSel)) {
    const empAtual = empresa ?? empresas.find(e => e.empcod === cab?.empcod)
    const destinatarioEhFornecedor = tipoCfg.modo === 'origem' && tipoCfg.destinatarioFixo !== 'emitente'
    const destNome = destinatarioEhFornecedor ? cab!.nome_fornecedor : (clienteSel!.cli_nom?.trim() || clienteSel!.cli_fan)
    const destDoc  = destinatarioEhFornecedor ? cab!.cnpj_fornecedor : clienteSel!.cli_mat
    const bloqueado = destinatarioEhFornecedor ? cab?.bloqueado : clienteSel?.bloqueado
    const linhasResumo = tipoCfg.modo === 'origem'
      ? itensSel.map(it => ({ descricao: it.descricao, unidade: it.unidade, qtd: Number(it.qtd_debito)||0, vUnit: Number(it.preco_unitario), ibsCst: it.ibsCbs.cst }))
      : itensManualValidos.map(it => ({ descricao: it.descricao, unidade: it.unidade, qtd: it.quantidade, vUnit: it.valorUnitario, ibsCst: it.ibsCbs.cst }))
    const totalRevisao = tipoCfg.modo === 'origem' ? totalOrigem : totalManual

    return (
      <>
      <div style={{ maxWidth:900, margin:'0 auto' }}>

        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:10 }}>Revisão da Nota de Débito — {tipoCfg.label}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px 24px', fontSize:'0.85rem' }}>
            <div><span style={{ color:'#9ca3af' }}>Empresa:</span> {empAtual?.empnom}</div>
            <div><span style={{ color:'#9ca3af' }}>Série/Nº:</span> {proxSerie}/{proxNumero}</div>
            <div><span style={{ color:'#9ca3af' }}>CFOP:</span> {nat?.tno_cdf} — {nat?.tno_dsc}</div>
            <div><span style={{ color:'#9ca3af' }}>Destinatário:</span> <strong>{destNome}</strong></div>
            <div><span style={{ color:'#9ca3af' }}>CNPJ:</span> {formatDoc(destDoc)}</div>
            <div><span style={{ color:'#9ca3af' }}>Data:</span> {dataEmissao}</div>
            {tipoCfg.modo === 'origem' && (
              <div style={{ gridColumn:'1/-1' }}>
                <span style={{ color:'#9ca3af' }}>{notasAdicionais.length > 0 ? `NF origem (${1 + notasAdicionais.length}):` : 'NF origem:'}</span>{' '}
                <span style={{ fontFamily:'monospace', fontSize:'0.75rem' }}>
                  {[cab!.chave, ...notasAdicionais.map(n => n.chave)].join(' · ')}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:8 }}>Itens ({linhasResumo.length})</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
            <thead>
              <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                <th style={{ padding:'5px 8px', textAlign:'left' }}>Descrição</th>
                <th style={{ padding:'5px 8px', textAlign:'center' }}>Un</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Qtd</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Vl Unit</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Total</th>
                <th style={{ padding:'5px 8px', textAlign:'center' }}>IBS/CBS</th>
              </tr>
            </thead>
            <tbody>
              {linhasResumo.map((it, idx) => (
                <tr key={idx} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ padding:'5px 8px' }}>{it.descricao}</td>
                  <td style={{ padding:'5px 8px', textAlign:'center', color:'#6b7280' }}>{it.unidade}</td>
                  <td style={{ padding:'5px 8px', textAlign:'right' }}>{it.qtd.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                  <td style={{ padding:'5px 8px', textAlign:'right', color:'#6b7280' }}>{it.vUnit.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                  <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:600 }}>{brl(r2(it.qtd*it.vUnit))}</td>
                  <td style={{ padding:'5px 8px', textAlign:'center', color: it.ibsCst?'#1d4ed8':'#9ca3af', fontSize:'0.75rem' }}>{it.ibsCst || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:'0.8rem', color:'#9ca3af' }}>{linhasResumo.length} {linhasResumo.length===1?'item':'itens'}</div>
              <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Total: {brl(totalRevisao)}</div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {erro && <span style={{ color:'#dc2626', fontSize:'0.82rem', maxWidth:300 }}>{erro}</span>}
              {tipoDebito === '04' && (
                <button onClick={abrirFinanceiro}
                  style={{ background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, padding: '0.35rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                  {finParcelas.length > 0 ? 'Financeiro ✓' : '+ Financeiro'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setEtapa(0)}>← Voltar</button>
              <button className="btn btn-primary" onClick={emitir} disabled={enviando || !!bloqueado} style={{ minWidth:160 }}>
                {enviando ? 'Transmitindo...' : editId ? 'Salvar e Transmitir →' : 'Emitir NF-e →'}
              </button>
            </div>
          </div>
        </div>

      </div>

      {financeiroModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1000, padding: '24px 12px',
        }} onClick={() => setFinanceiroModal(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, width: 560, maxWidth: '96vw',
            maxHeight: 'calc(100vh - 48px)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>

            <div style={{ background: '#065f46', color: '#fff', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>Financeiro</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                  Nº {proxNumero} — {destNome} — Total: {brl(totalRevisao)}
                </div>
              </div>
              <button onClick={() => setFinanceiroModal(false)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '1rem' }}>✕</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Qtd. Parcelas</label>
                  <input type="number" min={1} max={36} value={finQtd}
                    onChange={e => { const v = Math.max(1, Number(e.target.value)); setFinQtd(v); recalcularParcelas(v, finIntervalo) }}
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Intervalo (dias)</label>
                  <input type="number" min={1} value={finIntervalo}
                    onChange={e => { const v = Math.max(1, Number(e.target.value)); setFinIntervalo(v); recalcularParcelas(finQtd, v) }}
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }} />
                </div>
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>
                  Conta Financeira {finContaErro && <span style={{ color: '#dc2626' }}>*</span>}
                </label>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input type="text" value={finContaCod} placeholder="Código"
                    onChange={e => { setFinContaCod(e.target.value); setFinContaNome(''); setFinContaErro(null) }}
                    onBlur={async () => {
                      if (!finContaCod.trim()) return
                      try {
                        const r = await api.get('/erp/contas', { params: { busca: finContaCod.trim() } })
                        const found = r.data.find((c: any) => c.dep_cod.trim() === finContaCod.trim())
                        if (found) setFinContaNome(found.dep_des)
                      } catch { /* */ }
                    }}
                    style={{ width: 80, border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }} />
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input type="text" value={finContaNome} placeholder="Nome da conta"
                      onChange={async e => {
                        const v = e.target.value; setFinContaNome(v); setFinContaCod(''); setFinContaErro(null)
                        if (v.length >= 2) {
                          try {
                            const r = await api.get('/erp/contas', { params: { busca: v } })
                            setFinContaResultados(r.data); setFinContaAberto(true)
                          } catch { /* */ }
                        } else { setFinContaResultados([]); setFinContaAberto(false) }
                      }}
                      onBlur={() => setTimeout(() => setFinContaAberto(false), 150)}
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: '0.85rem' }} />
                    {finContaAberto && finContaResultados.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 180, overflowY: 'auto' }}>
                        {finContaResultados.map(c => (
                          <div key={c.dep_cod} onMouseDown={() => { setFinContaCod(c.dep_cod.trim()); setFinContaNome(c.dep_des); setFinContaAberto(false) }}
                            style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #f3f4f6' }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
                            onMouseLeave={e => (e.currentTarget.style.background = '')}>
                            <span style={{ fontFamily: 'monospace', color: '#6b7280', marginRight: 8 }}>{c.dep_cod.trim()}</span>{c.dep_des}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {finContaErro && <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: 4 }}>{finContaErro}</div>}
              </div>

              {finParcelas.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr className="thead-claro" style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'center', width: 60 }}>Parcela</th>
                      <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Vencimento</th>
                      <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Valor (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finParcelas.map((p, i) => (
                      <tr key={i}>
                        <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 700, color: '#6b7280' }}>{p.numero}</td>
                        <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                          <input type="date" value={p.vencimento}
                            onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, vencimento: e.target.value } : x))}
                            style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', width: '100%' }} />
                        </td>
                        <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                          <input type="number" value={p.valor}
                            onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, valor: e.target.value } : x))}
                            style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', textAlign: 'right', width: '100%' }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="thead-claro" style={{ background: '#f9fafb', fontWeight: 700 }}>
                      <td colSpan={2} style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Total</td>
                      <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', color: '#065f46' }}>
                        {finParcelas.reduce((s, p) => s + Number(p.valor), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', background: '#f9fafb' }}>
              <button onClick={() => { setFinanceiroModal(false); setFinContaErro(null) }} style={{ padding: '5px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.82rem', cursor: 'pointer', color: '#6b7280' }}>
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!finContaCod.trim()) { setFinContaErro('Informe a conta financeira'); return }
                  setFinanceiroModal(false)
                }}
                style={{ padding: '5px 18px', background: '#065f46', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    )
  }

  // ─── Etapa 0: Tipo + Busca/Destinatário + Configuração ───────────────────

  return (
    <div style={{ maxWidth:900, margin:'0 auto' }}>
      <AvisoUltimaNota empcod={empresa?.empcod ?? cab?.empcod ?? empresas[0]?.empcod} modelo="55" serie={proxSerie} proximo={proxNumero} onNumeroAjustado={n => setProxNumero(String(n))} />

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
          <label style={{ fontWeight:600, fontSize:'0.85rem', whiteSpace:'nowrap' }}>Estabelecimento</label>
          <select value={empresa?.empcod??''} onChange={e => setEmpresa(empresas.find(x=>x.empcod===Number(e.target.value))??null)}
            style={{ flex:1, padding:'6px 8px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.9rem' }}>
            {empresas.map(em => <option key={em.empcod} value={em.empcod}>{em.empnom}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Tipo de Débito</label>
          <select value={tipoDebito} onChange={e => trocarTipo(e.target.value)}
            style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.9rem' }}>
            {TIPOS_DEBITO.map(t => (
              <option key={t.codigo} value={t.codigo} disabled={!t.habilitado}>
                {t.label}{!t.habilitado ? ` (em breve — ${t.motivo})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {tipoCfg.modo === 'origem' ? (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:'0.95rem' }}>Buscar NF de origem (obrigatória para este tipo)</div>
            {proxNumero && (
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'4px 12px', fontSize:'0.82rem', color:'#1d4ed8', whiteSpace:'nowrap' }}>
                Próximo nº: <strong>Série {proxSerie} · Nº {proxNumero}</strong>
              </div>
            )}
          </div>
          {tipoCfg.destinatarioFixo === 'emitente' && (
            <div style={{ marginBottom:12, padding:'8px 12px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, color:'#1d4ed8', fontSize:'0.82rem' }}>
              Informe a chave da nota de <strong>entrada</strong> (compra) cujo crédito será anulado. O destinatário desta Nota de Débito será sempre <strong>{empresa?.empnom ?? 'a própria empresa'}</strong>, não o fornecedor.
            </div>
          )}
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Chave de acesso (44 dígitos)</label>
            <input value={chaveInput} onChange={e => setChaveInput(e.target.value.replace(/\D/g,'').slice(0,44))}
              placeholder="35250310..." maxLength={44}
              style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', fontFamily:'monospace', fontSize:'0.85rem', boxSizing:'border-box' }} />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'10px 0', color:'#9ca3af', fontSize:'0.8rem' }}>
            <div style={{ flex:1, height:1, background:'#e5e7eb' }} />ou buscar por campos<div style={{ flex:1, height:1, background:'#e5e7eb' }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr 80px 120px', gap:8, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Empcod</label>
              <input value={empcod} onChange={e=>setEmpcod(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>CNPJ/CPF Destinatário</label>
              <input value={cnpj} onChange={e=>setCnpj(e.target.value)} placeholder="00.000.000/0000-00" style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Série</label>
              <input value={serie} onChange={e=>setSerie(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Número</label>
              <input type="number" value={numBusca} onChange={e=>setNumBusca(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
          </div>
          <button className="btn btn-primary" onClick={buscar} disabled={buscando||!podeBuscar} style={{ width:'100%' }}>
            {buscando?'Buscando...':'Buscar'}
          </button>
          {erroBusca && <div style={{ marginTop:10, padding:'8px 12px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:6, color:'#dc2626', fontSize:'0.85rem' }}>{erroBusca}</div>}
        </div>
      ) : tipoCfg.destinatarioFixo === 'emitente' ? (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, marginBottom:8, fontSize:'0.95rem' }}>Destinatário</div>
          <div style={{ fontSize:'0.85rem', color:'#374151' }}>
            <strong>{clienteSel?.cli_nom}</strong> — {clienteSel && formatDoc(clienteSel.cli_mat)} (próprio emitente, fixo pra este tipo)
          </div>
          {proxNumero && (
            <div style={{ marginTop:8, background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'4px 12px', fontSize:'0.82rem', color:'#1d4ed8', display:'inline-block' }}>
              Próximo nº: <strong>Série {proxSerie} · Nº {proxNumero}</strong>
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, marginBottom:12, fontSize:'0.95rem' }}>Destinatário ({tipoCfg.destinatarioLabel})</div>
          {!clienteSel && (
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Nome, fantasia ou CNPJ/CPF</label>
              <div style={{ display:'flex', gap:8 }}>
                <input value={clienteBusca} onChange={e => handleBuscaCliente(e.target.value)} placeholder="Digite para buscar..." autoFocus
                  style={{ flex:1, padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
                {buscandoCliente && <span style={{ alignSelf:'center', fontSize:'0.8rem', color:'#9ca3af' }}>Buscando...</span>}
              </div>
              {clientesEncontrados.length > 0 && (
                <div style={{ marginTop:8, border:'1px solid #e5e7eb', borderRadius:6, maxHeight:220, overflowY:'auto' }}>
                  {clientesEncontrados.map(c => (
                    <button key={c.cli_cod} onClick={() => selecionarCliente(c)}
                      style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 10px', border:'none', borderBottom:'1px solid #f3f4f6', background:'#fff', cursor:'pointer' }}>
                      <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{c.cli_nom}</div>
                      <div style={{ fontSize:'0.75rem', color:'#6b7280' }}>{c.cli_fj==='J'?'CNPJ':'CPF'}: {formatDoc(c.cli_mat)}{c.cli_est?.trim() ? ` · UF ${c.cli_est}`:''}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {clienteSel && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.95rem' }}>{clienteSel.cli_nom}</div>
                  <div style={{ fontSize:'0.8rem', color:'#6b7280' }}>{clienteSel.cli_fj==='J'?'CNPJ':'CPF'}: {formatDoc(clienteSel.cli_mat)} · IE: {clienteSel.cli_ins?.trim() || 'Isento'}</div>
                  {clienteEndereco && (
                    <div style={{ fontSize:'0.8rem', color:'#6b7280' }}>
                      {clienteEndereco.logradouro}{clienteEndereco.numero?`, ${clienteEndereco.numero}`:''}{clienteEndereco.bairro?` - ${clienteEndereco.bairro}`:''}{clienteEndereco.cidade?`, ${clienteEndereco.cidade}/${clienteEndereco.uf}`:''}{clienteEndereco.cep?` - CEP ${clienteEndereco.cep}`:''}
                    </div>
                  )}
                </div>
                {proxNumero && (
                  <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'4px 12px', fontSize:'0.82rem', color:'#1d4ed8' }}>
                    Próximo nº: <strong>Série {proxSerie} · Nº {proxNumero}</strong>
                  </div>
                )}
              </div>
              {clienteSel.bloqueado && (
                <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginTop: '0.5rem' }}>
                  ⚠ DESTINATÁRIO BLOQUEADO — não é possível emitir documentos para ele.
                </div>
              )}
              <button onClick={() => { setClienteSel(null); setClienteBusca(''); setClienteEndereco(null) }}
                style={{ marginTop:8, fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>
                Trocar destinatário
              </button>
            </div>
          )}
        </div>
      )}

      {(tipoCfg.modo === 'origem' ? cab : clienteSel) && (
        <>
          {tipoCfg.modo === 'origem' && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:'0.95rem', color:'#374151', marginBottom:10 }}>Nota de Origem</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px 24px', fontSize:'0.85rem' }}>
                <div><span style={{ color:'#9ca3af' }}>Destinatário:</span> <strong>{cab!.nome_fornecedor}</strong></div>
                <div><span style={{ color:'#9ca3af' }}>CNPJ:</span> {formatDoc(cab!.cnpj_fornecedor)}</div>
                <div><span style={{ color:'#9ca3af' }}>IE:</span> {cab!.ie?.trim() || 'Isento'}</div>
                <div><span style={{ color:'#9ca3af' }}>UF:</span> {cab!.uf}</div>
                <div><span style={{ color:'#9ca3af' }}>Série/Nº:</span> {cab!.serie}/{cab!.numero}</div>
                <div><span style={{ color:'#9ca3af' }}>Emissão:</span> {cab!.data_emissao?.slice(0,10)}</div>
                <div><span style={{ color:'#9ca3af' }}>Total:</span> {brl(Number(cab!.valor_total))}</div>
                <div style={{ gridColumn:'1/-1' }}>
                  <span style={{ color:'#9ca3af' }}>Endereço:</span>{' '}
                  {cab!.logradouro}{cab!.numero_end?`, ${cab!.numero_end}`:''}{cab!.bairro?` - ${cab!.bairro}`:''}{cab!.cidade?`, ${cab!.cidade}/${cab!.uf}`:''}{cab!.cep?` - CEP ${cab!.cep}`:''}
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <span style={{ color:'#9ca3af' }}>Chave:</span>{' '}
                  <span style={{ fontFamily:'monospace', fontSize:'0.75rem' }}>{cab!.chave}</span>
                </div>
              </div>
              <div style={{ background: '#eef2ff', color: '#3730a3', fontSize: '0.8rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginTop: '0.75rem' }}>
                Destinatário herdado da nota de origem — campo bloqueado.
              </div>
              {cab!.bloqueado && (
                <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginTop: '0.5rem' }}>
                  ⚠ DESTINATÁRIO BLOQUEADO — não é possível emitir documentos para ele.
                </div>
              )}
            </div>
          )}

          {tipoDebito === '03' && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:'0.95rem', color:'#374151', marginBottom:10 }}>
                Outras NFs de Origem ({1 + notasAdicionais.length}/{LIMITE_NOTAS_ORIGEM})
              </div>
              <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:10 }}>
                Esse tipo pode juntar itens de várias notas não processadas na apuração, desde que sejam do mesmo fornecedor da primeira NF buscada acima.
              </div>
              {notasAdicionais.map(n => (
                <div key={n.chave} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderBottom:'1px solid #f3f4f6', fontSize:'0.82rem' }}>
                  <span style={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#6b7280', flex:1 }}>{n.chave}</span>
                  <span>Série/Nº: {n.serie}/{n.numero}</span>
                  <span>{brl(Number(n.valor_total))}</span>
                  <button onClick={() => removerNfAdicional(n.chave)}
                    style={{ fontSize:'0.75rem', padding:'2px 8px', borderRadius:4, border:'1px solid #fca5a5', background:'#fef2f2', color:'#dc2626', cursor:'pointer' }}>✕</button>
                </div>
              ))}
              {1 + notasAdicionais.length < LIMITE_NOTAS_ORIGEM && (
                <div style={{ display:'flex', gap:8, marginTop:10, alignItems:'center' }}>
                  <input value={chaveAdicionalInput} onChange={e => setChaveAdicionalInput(e.target.value.replace(/\D/g,'').slice(0,44))}
                    placeholder="Chave de acesso (44 dígitos)" maxLength={44}
                    style={{ flex:1, padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', fontFamily:'monospace', fontSize:'0.85rem' }} />
                  <button onClick={buscarNfAdicional} disabled={buscandoAdicional}
                    style={{ fontSize:'0.82rem', padding:'7px 14px', borderRadius:6, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>
                    {buscandoAdicional ? 'Buscando...' : '+ Adicionar NF'}
                  </button>
                </div>
              )}
              {erroAdicional && <div style={{ color:'#dc2626', fontSize:'0.8rem', marginTop:6 }}>{erroAdicional}</div>}
            </div>
          )}

          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, marginBottom:12, fontSize:'0.95rem' }}>Dados da Nota de Débito</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Natureza da Operação / CFOP</label>
              {tipoCfg.codigo === '06' && (
                <div style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.8rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: 8 }}>
                  ⚠ Só emita esta nota quando tiver certeza do item que está sendo vendido — ele entra na NF-e e não pode mais ser alterado depois. Use CFOP <strong>5922</strong> se o cliente for do RS, ou <strong>6922</strong> se for de fora do RS.
                </div>
              )}
              <NaturezaSelect
                naturezas={tipoCfg.cfopPermitidos ? naturezas.filter(n => tipoCfg.cfopPermitidos!.includes(n.tno_cdf)) : naturezas}
                value={naturezaCod}
                onChange={setNaturezaCod}
              />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'160px 1fr', gap:12 }}>
              <div>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Data Emissão</label>
                <input type="date" value={dataEmissao} onChange={e=>setDataEmissao(e.target.value)}
                  style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Observação</label>
                <input value={obs} onChange={e=>setObs(e.target.value)} maxLength={60} placeholder="Opcional"
                  style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
              </div>
            </div>
            {tipoCfg.nfOrigemOpcional && (
              <div style={{ marginTop:14 }}>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>
                  Chave da NF de origem (opcional — só referência, não herda nada)
                </label>
                <input value={nfOrigemOpcionalChave} onChange={e => setNfOrigemOpcionalChave(e.target.value.replace(/\D/g,'').slice(0,44))}
                  placeholder="Deixe em branco se não souber de qual compra veio" maxLength={44}
                  style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', fontFamily:'monospace', fontSize:'0.85rem', boxSizing:'border-box' }} />
              </div>
            )}
            {tipoDebito === '04' && (
              <div style={{ marginTop:14, background: '#fffbeb', color: '#92400e', fontSize: '0.8rem', padding: '0.5rem 0.75rem', borderRadius: 4 }}>
                Este é o único tipo que gera fatura. Depois de autorizada, lance as parcelas e a conta pelo botão "Financeiro" na tela <strong>Lista NF-e</strong> — igual já é feito hoje pras demais notas.
              </div>
            )}
            {tipoCfg.modo === 'origem' && tipoCfg.cstMode === 'regra' && (
              <div style={{ marginTop:14 }}>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>
                  IBS/CBS — Regra (aplicada a todos os itens){tipoCfg.regraEsperada ? ` — esperado ${tipoCfg.regraEsperada}` : ''}
                </label>
                <IbsCbsSelect codValue={ibsCbsCod} desValue={ibsCbsDes} onChange={atualizarIbsCbsOrigem} triPermitidos={tipoCfg.ibsCbsTriPermitidos} />
              </div>
            )}
          </div>

          {/* Itens — modo origem: herdados da nota original (só quantidade e IBS/CBS) */}
          {tipoCfg.modo === 'origem' && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ fontWeight:700, fontSize:'0.95rem' }}>Itens</span>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:true,qtd_debito:String(it.quantidade)})))}
                    style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Todos</button>
                  <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:false})))}
                    style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Nenhum</button>
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                  <thead>
                    <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                      <th style={{ padding:'6px 8px', width:32 }}></th>
                      <th style={{ padding:'6px 8px', textAlign:'left' }}>Descrição</th>
                      {notasAdicionais.length > 0 && <th style={{ padding:'6px 8px', textAlign:'center' }}>NF Origem</th>}
                      <th style={{ padding:'6px 8px', textAlign:'center' }}>Un</th>
                      {tipoDebito !== '04' && <th style={{ padding:'6px 8px', textAlign:'right' }}>Qtd Original</th>}
                      {tipoDebito !== '04' && <th style={{ padding:'6px 8px', textAlign:'right' }}>Qtd a Debitar</th>}
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>{tipoDebito === '04' ? 'Valor da Multa/Juros' : 'Vl Unit'}</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Total</th>
                      <th style={{ padding:'6px 8px', textAlign:'center' }}>IBS/CBS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const numColsOrigem = 6 + (notasAdicionais.length > 0 ? 1 : 0) + (tipoDebito !== '04' ? 2 : 0)
                      return itens.map((it, idx) => {
                      const qtdDebito = Number(it.qtd_debito)||0
                      const qtdOrig  = Number(it.quantidade)
                      const excede   = tipoDebito !== '04' && it.selecionado && qtdDebito > qtdOrig
                      const parcial  = tipoDebito !== '04' && it.selecionado && qtdDebito > 0 && qtdDebito < qtdOrig
                      const bordaCor = excede?'#f87171':parcial?'#fbbf24':'#d1d5db'
                      const bgInput  = excede?'#fff1f2':parcial?'#fffbeb':undefined
                      const totalIt  = r2(qtdDebito * Number(it.preco_unitario))
                      return (
                        <Fragment key={idx}>
                          <tr style={{ borderBottom: (excede||parcial)?'none':'1px solid #f3f4f6', background:it.selecionado?'#fff':'#fafafa', opacity:it.selecionado?1:0.5 }}>
                            <td style={{ padding:'6px 8px', textAlign:'center' }}>
                              <input type="checkbox" checked={it.selecionado} onChange={() => editItem(idx,'selecionado',!it.selecionado)} />
                            </td>
                            <td style={{ padding:'6px 8px', fontWeight:500 }}>{it.descricao}</td>
                            {notasAdicionais.length > 0 && (
                              <td style={{ padding:'6px 8px', textAlign:'center', color:'#6b7280', fontFamily:'monospace', fontSize:'0.7rem' }}>
                                ...{(it.origemChave || cab!.chave).slice(-8)}
                              </td>
                            )}
                            <td style={{ padding:'6px 8px', textAlign:'center', color:'#6b7280' }}>{it.unidade}</td>
                            {tipoDebito !== '04' && <td style={{ padding:'6px 8px', textAlign:'right', color:'#6b7280' }}>{qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>}
                            {tipoDebito !== '04' && (
                              <td style={{ padding:'6px 4px', textAlign:'right' }}>
                                <input type="number" value={it.qtd_debito} onChange={e=>editItem(idx,'qtd_debito',e.target.value)} disabled={!it.selecionado} min={0} step="0.001"
                                  style={{ width:80, textAlign:'right', padding:'3px 6px', borderRadius:4, border:`1px solid ${bordaCor}`, background:bgInput, fontSize:'0.82rem' }} />
                              </td>
                            )}
                            <td style={{ padding:'6px 4px', textAlign:'right' }}>
                              {tipoDebito === '04' ? (
                                <input type="number" value={it.preco_unitario || ''} onChange={e=>editItem(idx,'preco_unitario',e.target.value)} disabled={!it.selecionado} min={0} step="0.01"
                                  placeholder="0,00"
                                  style={{ width:100, textAlign:'right', padding:'3px 6px', borderRadius:4, border:'1px solid #d1d5db', fontSize:'0.82rem' }} />
                              ) : (
                                Number(it.preco_unitario).toLocaleString('pt-BR',{minimumFractionDigits:2})
                              )}
                            </td>
                            <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:600 }}>{brl(totalIt)}</td>
                            <td style={{ padding:'6px 8px', textAlign:'center' }}>
                              <button onClick={() => editItem(idx,'expandido',!it.expandido)} disabled={!it.selecionado}
                                style={{ fontSize:'0.75rem', padding:'2px 8px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer', color: it.ibsCbs.cst?'#1d4ed8':'#374151' }}>
                                {it.ibsCbs.cst || '—'} {it.expandido ? '▲' : '▼'}
                              </button>
                            </td>
                          </tr>
                          {(excede || parcial) && (
                            <tr key={`${idx}-aviso`}>
                              <td colSpan={notasAdicionais.length > 0 ? 9 : 8} style={{ padding:'2px 8px 6px 44px', fontSize:'0.75rem', color:excede?'#dc2626':'#92400e' }}>
                                {excede ? `Quantidade maior que a original (${qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})})` : `Débito parcial — original: ${qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})}`}
                              </td>
                            </tr>
                          )}
                          {it.expandido && (
                            <tr style={{ borderBottom:'1px solid #e5e7eb', background:'#f8fafc' }}>
                              <td colSpan={numColsOrigem} style={{ padding:'10px 16px 14px 36px' }}>
                                {it.ibsCbs.cst ? (
                                  <>
                                    <table className="imp-table">
                                      <thead>
                                        <tr>
                                          <th style={{ width:70 }}>Tributo</th>
                                          <th style={{ width:125 }}>Base Cálculo</th>
                                          <th style={{ width:75 }}>Alíquota</th>
                                          <th style={{ width:125 }}>Destaque</th>
                                          <th style={{ width:60 }}>CST</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {[
                                          { label:'CBS',     bc: it.ibsCbs.cbsBcEf,    aliq: it.ibsCbs.cbsAliEf,    valor: it.ibsCbs.cbsValor },
                                          { label:'IBS-UF',  bc: it.ibsCbs.ibsUfBcEf,  aliq: it.ibsCbs.ibsUfAliEf,  valor: it.ibsCbs.ibsUfValor },
                                          { label:'IBS-Mun', bc: it.ibsCbs.ibsMunBcEf, aliq: it.ibsCbs.ibsMunAliEf, valor: it.ibsCbs.ibsMunValor },
                                        ].map(row => (
                                          <tr key={row.label}>
                                            <td className="imp-label">{row.label}</td>
                                            <td>{fmtMoeda(row.bc)}</td>
                                            <td>{row.aliq.toLocaleString('pt-BR',{minimumFractionDigits:2})}%</td>
                                            <td>{fmtMoeda(row.valor)}</td>
                                            <td>{it.ibsCbs.cst}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {it.ibsCbs.classTrib && (
                                      <div style={{ marginTop:'0.35rem', fontSize:'0.75rem', color:'#6b7280' }}>
                                        <strong>Class. Trib.:</strong> {it.ibsCbs.classTrib} — {it.ibsCbs.classTribDes}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div style={{ padding:'6px 0', fontSize:'0.82rem', color:'#9ca3af' }}>Nenhuma regra IBS/CBS herdada da origem</div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Itens — modo manual: informados pelo usuário (ex.: tipos 01, 05) */}
          {tipoCfg.modo === 'manual' && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:10 }}>Itens</div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:'left', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Produto</th>
                    <th style={{ width:'8%', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Qtd</th>
                    <th style={{ width:'15%', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Vl. Unit.</th>
                    <th style={{ width:'13%', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Total</th>
                    <th style={{ width:'8%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {itensManual.map((it, i) => (
                    <Fragment key={i}>
                      <tr>
                        <td style={{ padding:'0.35rem 0.5rem', verticalAlign:'top' }}>
                          <ItemSelect somenteAtivos
                            codValue={it.cProd} descValue={it.descricao} unValue={it.unidade}
                            onChange={(cod, des, un, _precod, ncm, cest) => {
                              setItensManual(prev => prev.map((x, idx) => idx===i ? {
                                ...x, cProd: cod, descricao: des, unidade: un,
                                NCM: ncm?.replace(/\D/g,'') || x.NCM, CEST: cest?.replace(/\D/g,'') || x.CEST,
                                saldoEstoque: undefined,
                              } : x))
                              if (cod && tipoCfg.custoMedioAutomatico) buscarCustoMedio(i, cod)
                            }}
                          />
                        </td>
                        <td style={{ verticalAlign:'top' }}><input type="number" min={0} value={it.quantidade} onChange={e => updateItemManual(i, 'quantidade', Number(e.target.value))}
                          style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} /></td>
                        <td style={{ verticalAlign:'top' }}>
                          <input type="number" min={0} step={0.0001} value={it.valorUnitario} onChange={e => updateItemManual(i, 'valorUnitario', Number(e.target.value))}
                            style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
                          {tipoCfg.custoMedioAutomatico && (
                            <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:2 }}>
                              {it.buscandoCustoMedio ? 'buscando custo médio...' : it.saldoEstoque !== undefined ? `custo médio de estoque · saldo atual: ${it.saldoEstoque.toLocaleString('pt-BR',{minimumFractionDigits:2})}` : 'selecione o item pra buscar o custo médio'}
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight:600, verticalAlign:'top', paddingTop:'0.5rem' }}>{brl(it.quantidade * it.valorUnitario)}</td>
                        <td style={{ verticalAlign:'top', paddingTop:'0.35rem' }}>
                          {itensManual.length > 1 && (
                            <button onClick={() => removeItemManual(i)}
                              style={{ fontSize:'0.75rem', padding:'2px 8px', borderRadius:4, border:'1px solid #fca5a5', background:'#fef2f2', color:'#dc2626', cursor:'pointer' }}>✕</button>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={5} style={{ padding:'0 0.5rem 0.75rem' }}>
                          <label style={{ fontSize:'0.78rem', color:'#6b7280', display:'block', marginBottom:4 }}>
                            IBS/CBS (Regra){tipoCfg.regraEsperada ? ` — esperado ${tipoCfg.regraEsperada}` : ''}
                          </label>
                          <IbsCbsSelect
                            codValue={it.ibsCbsRegra} desValue={it.ibsCbsDes}
                            triPermitidos={tipoCfg.ibsCbsTriPermitidos}
                            onChange={async (cod, des) => {
                              const total = it.quantidade * it.valorUnitario
                              let ibsCbs = { ...ibsCbsVazio }
                              if (cod) {
                                try {
                                  const r = await api.get(`/erp/ibscbs/${cod}`)
                                  if (r.data) ibsCbs = calcIbsCbs(total, r.data)
                                } catch {}
                              }
                              setItensManual(prev => prev.map((x, idx) => idx===i ? { ...x, ibsCbsRegra: cod, ibsCbsDes: des, ibsCbs } : x))
                            }}
                          />
                        </td>
                      </tr>
                      {it.ibsCbs.cst && (
                        <tr>
                          <td colSpan={5} style={{ padding:'0 0.5rem 0.75rem' }}>
                            <table className="imp-table">
                              <thead>
                                <tr>
                                  <th style={{ width:70 }}>Tributo</th>
                                  <th style={{ width:125 }}>Base Cálculo</th>
                                  <th style={{ width:75 }}>Alíquota</th>
                                  <th style={{ width:125 }}>Destaque</th>
                                  <th style={{ width:60 }}>CST</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { label:'CBS',     bc: it.ibsCbs.cbsBcEf,    aliq: it.ibsCbs.cbsAliEf,    valor: it.ibsCbs.cbsValor },
                                  { label:'IBS-UF',  bc: it.ibsCbs.ibsUfBcEf,  aliq: it.ibsCbs.ibsUfAliEf,  valor: it.ibsCbs.ibsUfValor },
                                  { label:'IBS-Mun', bc: it.ibsCbs.ibsMunBcEf, aliq: it.ibsCbs.ibsMunAliEf, valor: it.ibsCbs.ibsMunValor },
                                ].map(row => (
                                  <tr key={row.label}>
                                    <td className="imp-label">{row.label}</td>
                                    <td>{fmtMoeda(row.bc)}</td>
                                    <td>{row.aliq.toLocaleString('pt-BR',{minimumFractionDigits:2})}%</td>
                                    <td>{fmtMoeda(row.valor)}</td>
                                    <td>{it.ibsCbs.cst}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      {tipoCfg.tributosClassicos && (
                        <tr>
                          <td colSpan={5} style={{ padding:'0 0.5rem 0.75rem' }}>
                            <table className="imp-table">
                              <thead>
                                <tr>
                                  <th style={{ width:70 }}>Tributo</th>
                                  <th style={{ width:125 }}>Base Cálculo</th>
                                  <th style={{ width:75 }}>Alíquota</th>
                                  <th style={{ width:125 }}>Destaque</th>
                                  <th style={{ width:60 }}>CST</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { label:'ICMS',   bc:'edit_icms_base',   aliq:'edit_icms_aliq',   dest:'edit_icms_valor',   cst:'edit_icms_cst' },
                                  { label:'IPI',    bc:'edit_ipi_base',    aliq:'edit_ipi_aliq',    dest:'edit_ipi_valor',    cst:'edit_ipi_cst' },
                                  { label:'PIS',    bc:'edit_pis_base',    aliq:'edit_pis_aliq',    dest:'edit_pis_valor',    cst:'edit_pis_cst' },
                                  { label:'COFINS', bc:'edit_cofins_base', aliq:'edit_cofins_aliq', dest:'edit_cofins_valor', cst:'edit_cofins_cst' },
                                ].map(row => (
                                  <tr key={row.label}>
                                    <td className="imp-label">{row.label}</td>
                                    <td><input type="number" className="imp-input" min={0} step={0.01}
                                      value={(it as any)[row.bc]}
                                      onChange={e => updateItemManual(i, row.bc as keyof ItemManual, Number(e.target.value))} /></td>
                                    <td><input type="number" className="imp-input imp-aliq" min={0} step={0.001}
                                      value={(it as any)[row.aliq]}
                                      onChange={e => updateItemManual(i, row.aliq as keyof ItemManual, Number(e.target.value))} /></td>
                                    <td><input type="number" className="imp-input" min={0} step={0.01}
                                      value={(it as any)[row.dest]}
                                      onChange={e => updateItemManual(i, row.dest as keyof ItemManual, Number(e.target.value))} /></td>
                                    <td><input type="text" className="imp-input imp-cst"
                                      value={(it as any)[row.cst]}
                                      onChange={e => updateItemManual(i, row.cst as keyof ItemManual, e.target.value)} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              <button onClick={addItemManual}
                style={{ marginTop:8, fontSize:'0.82rem', padding:'5px 12px', borderRadius:6, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>
                + Adicionar item
              </button>
            </div>
          )}

          <div className="card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'0.8rem', color:'#9ca3af' }}>
                  {tipoCfg.modo === 'origem'
                    ? `${itensSel.length} ${itensSel.length===1?'item':'itens'} selecionado${itensSel.length!==1?'s':''}`
                    : `${itensManualValidos.length} ${itensManualValidos.length===1?'item':'itens'}`}
                </div>
                <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Total: {brl(tipoCfg.modo === 'origem' ? totalOrigem : totalManual)}</div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setEtapa(1)}
                disabled={
                  !naturezaCod ||
                  (tipoCfg.modo === 'origem'
                    ? (itensSel.length===0 || temQtdInvalida || cab?.bloqueado)
                    : (itensManualValidos.length===0 || clienteSel?.bloqueado))
                }
                style={{ minWidth:180 }}
              >
                Revisar →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
